import * as React from 'react';
import type { InjectedIntlProps } from 'react-intl';
import { injectIntl } from 'react-intl';
import type { CmdbObject } from 'rest/cmdb-object-picker';
import type { ValueType } from '@atlaskit/select';

import type { FieldFormProps } from '@atlassian/help-center-common-component/fields/types';
import { FieldSingleSelect, FormMultiSelect } from '@atlassian/help-center-common-component/form-select';

import { toMultiSelectValue } from '@atlassian/help-center-common-util/select';
import { CmdbSelectOption } from '../cmdb-object-picker-option';

import messages from './messages';

export interface CmdbObjectOptionGroup {
    options: CmdbObjectPickerOption[];
    label: string;
}

export interface CmdbObjectPickerOption {
    label: CmdbObject['label'];
    value: CmdbObject['id'];
    cmdbObject: CmdbObject;
}

export type InputActionTypes = 'set-value' | 'input-change' | 'input-blur' | 'menu-close';

export interface InputActionMeta {
    action: InputActionTypes;
}

export interface CmdbObjectPickerDumbProps extends FieldFormProps<string[]> {
    label: string;
    loadOptions: (inputValue: string, callback: (options: CmdbObjectOptionGroup[]) => void) => void;
    isLoading?: boolean;
    doInitialSearch?: boolean; // Whether we do an initial loadOptions("")
    isMulti: boolean;
    filterAttributes: string[];
    limit: number;
    hasValidConfig: boolean;
    onInputChange: (input: string, type: InputActionMeta) => void;
    options: CmdbObjectOptionGroup[];
    onFocus: () => void;
    defaultValue: CmdbObjectPickerOption[] | void;
    populateDefaultValue: boolean;
    classNamePrefix?: string;
}

export class CmdbObjectPickerDumb extends React.Component<CmdbObjectPickerDumbProps & InjectedIntlProps> {
    onChangeForSelect = (selectedOption: ValueType<CmdbObjectPickerOption>) => {
        this.props.onChange?.(
            this.props.id,
            toMultiSelectValue(selectedOption).map((v) => v.value)
        );
    };

    customComponents = {
        Option: CmdbSelectOption,
    };

    noOptionsMessage = () => {
        const { intl } = this.props;
        return intl.formatMessage(messages.noOptions);
    };

    render() {
        const { isMulti, disabled, doInitialSearch, intl, defaultValue, populateDefaultValue, ...otherProps } =
            this.props;

        const placeholder = intl.formatMessage(messages.searchPlaceholder);

        if (isMulti) {
            return (
                <FormMultiSelect
                    {...otherProps}
                    actionSubjectId="cmdbObjectPickerField"
                    placeholder={placeholder}
                    noOptionsMessage={this.noOptionsMessage}
                    onChange={this.onChangeForSelect}
                    components={this.customComponents}
                    value={populateDefaultValue ? defaultValue : undefined}
                />
            );
        }

        return (
            <FieldSingleSelect
                {...otherProps}
                actionSubjectId="cmdbObjectPickerField"
                placeholder={placeholder}
                noOptionsMessage={this.noOptionsMessage}
                onChange={this.onChangeForSelect}
                components={this.customComponents}
                value={populateDefaultValue && defaultValue ? defaultValue[0] : undefined}
                isClearable
            />
        );
    }
}

export default injectIntl(CmdbObjectPickerDumb);
